import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import { v4 as uuidv4 } from 'uuid';

import OpeningListLoader from '../../../components/shared/SkeletonLoaders/OpeningListLoader/OpeningListLoader';
import { IsMobile } from '../../../helpers/IsMobile';
import { getOpeningDetail } from '../../../redux/actions/browseActions';
import { selectUserUuid } from '../../../selectors/authSelectors.js';
import {
  selectLoadingJobs,
  selectMoreJobsloading,
  selectOpeningDetails,
  selectOpeningDetailsLoading,
  selectOpeningList,
  selectOpeningListLoading,
  selectPaginationInfo,
} from '../../../selectors/browseSelectors.js';
import { selectOrganizationLoading } from '../../../selectors/organizationSelectors.js';
import EmptyOpeningList from '../EmptyOpeningList/EmptyOpeningList';
import OpeningsListElement from '../OpeningsListElement/OpeningsListElement';

import styles from './OpeningsListWrapper.module.scss';

const OpeningsListWrapper = ({ setPaginationNo, paginationNo, isPublic = false }) => {
  const { t } = useTranslation('common');
  const dispatch = useDispatch();
  const isMobile = IsMobile();

  const [noJobs, setNoJobs] = useState(false);
  const openings = useSelector(selectOpeningList);
  const openingsList = [...new Map(openings.map((item) => [item.id, item])).values()];
  const paginationInfo = useSelector(selectPaginationInfo);
  const openingDetails = useSelector(selectOpeningDetails);
  const openingDetailsLoading = useSelector(selectOpeningDetailsLoading);
  const userUuid = useSelector(selectUserUuid);
  const isLoadingJobs = useSelector(selectLoadingJobs);
  const isLoading = useSelector(selectOpeningListLoading);
  const moreJobsLoading = useSelector(selectMoreJobsloading);
  const isLoadingOrganization = useSelector(selectOrganizationLoading);
  const currentOrgLoading = useSelector((state) => state.organizationsReducer.currentOrganizationLoading);

  const listRef = useRef();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('id');
  const loaders = Array(8).fill(0);

  useEffect(() => {
    if (isLoadingOrganization && jobId && openingDetails.id !== jobId && openingsList.length > 0 && !isLoadingJobs) {
      dispatch(getOpeningDetail(jobId, userUuid));
    }
  }, [isLoadingOrganization, jobId, openingsList.length, isLoadingJobs, dispatch, userUuid]);

  const calcHeight = () => {
    if (listRef.current) {
      const viewportHeight = window.innerHeight;
      const listWrapper = document.querySelector('.list-wrapper');
      const listWrapperOffsetTop = listWrapper.getBoundingClientRect().top;
      let remainingHeight = viewportHeight - listWrapperOffsetTop;
      if (isMobile) {
        remainingHeight -= 60;
      }

      listWrapper.style.height = `${remainingHeight}px`;
    }
  };

  const handleScroll = () => {
    const { scrollTop, scrollHeight, clientHeight } = listRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 5) {
      if (paginationNo < paginationInfo.pages && !moreJobsLoading) {
        setPaginationNo(parseInt(paginationNo, 10) + 1);
      } else if (paginationNo === paginationInfo.pages) {
        setNoJobs(true);
      }
    }
  };

  useEffect(() => {
    calcHeight();
  }, [listRef.current]);

  useEffect(() => {
    calcHeight();
  }, [window.innerHeight]);

  const isAnyLoading = isLoading || currentOrgLoading || isLoadingJobs || openingDetailsLoading;

  const showEmptyOrLoading = () => {
    if (isAnyLoading) {
      return loaders.map(() => <OpeningListLoader key={uuidv4()} background />);
    }
    if (!isAnyLoading && openingsList.length === 0 && Object.keys(openingDetails).length === 0) {
      return <EmptyOpeningList />;
    }
    return null;
  };

  return (
    <div className={classNames(styles.openingListWrapper, 'list-wrapper')} ref={listRef} onScroll={handleScroll}>
      <div className={styles.openingListContainer}>
        {showEmptyOrLoading()}
        {openingsList.map((opening) => (
          <OpeningsListElement
            key={opening.uuid}
            addBorder={opening.uuid === openingDetails?.uuid}
            opening={opening}
            handleOpeningSelect={(id) => dispatch(getOpeningDetail(id, userUuid))}
            isPublic={isPublic}
          />
        ))}
        {!moreJobsLoading && noJobs && <div className={styles.noJobs}>{t('browse.allJobsLoaded')}</div>}
        {moreJobsLoading && (
          <div className={styles.loading}>
            <span className={styles.loadingText}>{t('loading')}...</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OpeningsListWrapper;
